// src/components/ProtectedRoute.js
// OPTIMIZADO: Usa Firebase Auth custom claims en lugar de Firestore role field
import schoolConfig from '../config/schools';
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { navigate } from "gatsby";
import { hasRouteAccess, deniedRedirectPath } from "../utils/routeAccess";

const ProtectedRoute = ({ children, adminOnly = false, requiredRole }) => {
  const { currentUser, isAdmin, isTutor, loading } = useAuth();
  const { openLogin, isLoginOpen } = useModal();
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");
  const [modalDismissed, setModalDismissed] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (!currentUser) {
      setCheckingAccess(false);
      return;
    }

    const checkAccess = () => {
      const accessGranted = hasRouteAccess({ isAdmin, isTutor, adminOnly, requiredRole });

      if (!accessGranted) {
        setAccessDeniedMessage("No tienes permisos para acceder a esta página.");
        setShowAccessDenied(true);
        if (typeof window !== 'undefined') {
          setTimeout(() => navigate(deniedRedirectPath({ isTutor })), 2000);
        }
      }

      setHasAccess(accessGranted);
      setCheckingAccess(false);
    };

    checkAccess();
  }, [currentUser, isAdmin, isTutor, loading, adminOnly, requiredRole]);

  // Auto-open login modal when unauthenticated
  useEffect(() => {
    if (!loading && !currentUser && !checkingAccess && !modalDismissed) {
      const returnPath = typeof window !== 'undefined' ? window.location.pathname : null;
      openLogin('protected-route', returnPath);
    }
  }, [loading, currentUser, checkingAccess, modalDismissed]);

  // Detect when user closes the modal without logging in
  useEffect(() => {
    if (!isLoginOpen && !currentUser && !loading && !checkingAccess) {
      setModalDismissed(true);
    }
  }, [isLoginOpen, currentUser, loading, checkingAccess]);

  // Loading
  if (loading || checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="text-center">
          <img src="/images/Logo/Logo Icon SFS.png" alt="SFS" className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <div className="w-5 h-5 border-2 border-[#E76F51]/30 border-t-[#E76F51] rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Not authenticated — show background while modal is open, fallback card if dismissed
  if (!currentUser) {
    if (!modalDismissed) {
      // Modal is open (or about to open) — just show a clean background
      return (
        <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
          <img src="/images/Logo/Logo Icon SFS.png" alt="SFS" className="w-14 h-14 opacity-20" />
        </div>
      );
    }

    // User dismissed the modal — show a friendly fallback
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="text-center max-w-sm px-6">
          <img src="/images/Logo/Logo Icon SFS.png" alt="SFS" className="w-14 h-14 mx-auto mb-6 opacity-60" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-2">
            Inicia sesión para continuar
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            Este contenido es exclusivo para estudiantes de {schoolConfig.name}.
          </p>
          <button
            onClick={() => {
              setModalDismissed(false);
              const returnPath = typeof window !== 'undefined' ? window.location.pathname : null;
              openLogin('protected-route', returnPath);
            }}
            className="w-full py-3 bg-[#E76F51] text-white font-semibold rounded-xl hover:bg-[#d35f43] transition-colors mb-3"
          >
            Iniciar Sesión
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 text-slate-500 text-sm hover:text-slate-700 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Access denied
  if (showAccessDenied) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="text-center max-w-sm px-6">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-[#0F172A] mb-2">Sin permisos</h2>
          <p className="text-slate-500 text-sm mb-4">{accessDeniedMessage}</p>
          <p className="text-xs text-slate-400">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return hasAccess ? <>{children}</> : null;
};

export default ProtectedRoute;
